import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { apiClient } from '../../lib/api-client';
import { ConversationView, type ConversationMessage } from './ConversationView';

interface RunInspectionDialogProps {
  open: boolean;
  onClose: () => void;
  runId: string;
}

type InspectTab = 'overview' | 'conversation' | 'stages' | 'raw';

function formatJson(data: unknown): string {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function stageLabel(value: string): string {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function RunInspectionDialog({ open, onClose, runId }: RunInspectionDialogProps) {
  const [tab, setTab] = useState<InspectTab>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspectionData, setInspectionData] = useState<any>(null);
  const [conversationData, setConversationData] = useState<any>(null);

  useEffect(() => {
    if (!open || !runId) return;
    setLoading(true);
    setError(null);
    setTab('overview');
    void Promise.all([
      apiClient.getAiWorkflowRunInspection(runId),
      apiClient.getAiWorkflowRunConversation(runId),
    ])
      .then(([inspection, conversation]) => {
        setInspectionData(inspection);
        setConversationData(conversation);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load inspection');
      })
      .finally(() => setLoading(false));
  }, [open, runId]);

  const run = inspectionData?.run;
  const summary = inspectionData?.summary;
  const inspection = inspectionData?.inspection;
  const timeline = inspection?.stageTimeline || {};

  const conversation = useMemo<ConversationMessage[]>(() => {
    const messages = Array.isArray(conversationData?.conversation) ? conversationData.conversation : [];
    return messages.map((entry: any, index: number) => ({
      id: String(entry?.id || `msg-${index}`),
      role: entry?.role === 'system' ? 'system' : entry?.role === 'user' ? 'user' : entry?.role === 'tool' ? 'tool' : 'assistant',
      content: String(entry?.content || ''),
      timestamp: typeof entry?.timestamp === 'string' ? entry.timestamp : undefined,
      stage: typeof entry?.stage === 'string' ? stageLabel(entry.stage) : undefined,
      toolName: typeof entry?.toolName === 'string' ? entry.toolName : undefined,
      toolInput: typeof entry?.toolInput === 'string' ? entry.toolInput : undefined,
      toolOutput: typeof entry?.toolOutput === 'string' ? entry.toolOutput : undefined,
      durationMs: typeof entry?.durationMs === 'number' ? entry.durationMs : undefined,
    }));
  }, [conversationData]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              Run Inspection
              {run?.currentStage ? <Badge variant="outline">{stageLabel(run.currentStage)}</Badge> : null}
            </span>
          </DialogTitle>
          <DialogDescription>{summary?.displayTitle || run?.runId || runId}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {([
              { key: 'overview', label: 'Overview' },
              { key: 'conversation', label: 'Conversation' },
              { key: 'stages', label: 'Stages' },
              { key: 'raw', label: 'Raw Data' },
            ] as Array<{ key: InspectTab; label: string }>).map((item) => (
              <Button
                key={item.key}
                size="sm"
                variant={tab === item.key ? 'default' : 'outline'}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-20 rounded" />
              <Skeleton className="h-48 rounded" />
            </div>
          )}

          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && tab === 'overview' && (
            <div className="space-y-4">
              <section className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Run ID</p>
                  <p className="font-mono text-xs">{run?.runId}</p>
                </div>
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Stream</p>
                  <p className="text-xs">{run?.streamId}</p>
                </div>
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Workflow</p>
                  <p className="text-xs">{run?.workflowType}</p>
                </div>
                <div className="rounded border border-gray-200 p-2 dark:border-gray-700">
                  <p className="text-[10px] uppercase text-gray-500">Triggered By</p>
                  <p className="text-xs">{run?.triggeredBy}</p>
                </div>
              </section>
              <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Lobby Decision</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {inspection?.providers?.lobbyOpenAi?.decisionSummary || inspection?.ingest?.decisionSummary || 'No decision summary captured.'}
                </p>
              </section>
              <section className="rounded border border-gray-200 p-3 dark:border-gray-700">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Ingest</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {inspection?.ingest?.effectiveContent || 'No ingest content captured.'}
                </p>
              </section>
            </div>
          )}

          {!loading && !error && tab === 'conversation' && (
            <ConversationView messages={conversation} emptyLabel="No run conversation captured." />
          )}

          {!loading && !error && tab === 'stages' && (
            <div className="space-y-2">
              {(timeline?.attempts || []).length === 0 ? (
                <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  No stage attempts captured.
                </div>
              ) : (timeline.attempts || []).map((attempt: any) => (
                <div key={attempt.attemptId} className="rounded border border-gray-200 p-3 dark:border-gray-700">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant={attempt.status === 'failed' ? 'destructive' : attempt.status === 'completed' ? 'success' : 'outline'}>
                      {stageLabel(attempt.status)}
                    </Badge>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {stageLabel(attempt.displayStep || attempt.action)}
                    </span>
                    {attempt.startedAt ? <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{new Date(attempt.startedAt).toLocaleString()}</span> : null}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{attempt.decisionSummary || attempt.triggerReason}</p>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && tab === 'raw' && (
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Inspection</p>
                <pre className="max-h-[55vh] overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                  {formatJson(inspection)}
                </pre>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Conversation</p>
                <pre className="max-h-[55vh] overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                  {formatJson(conversationData)}
                </pre>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
